<template>
  <div class="sim-trading page-shell">
    <!-- 工具条：账户 + 关联策略 + 行情新鲜度 -->
    <div class="sim-bar">
      <el-select
        v-model="accountId"
        size="small"
        style="width: 190px"
        placeholder="选择模拟账户"
        @change="onAccountChange"
      >
        <el-option
          v-for="a in accounts"
          :key="a.id"
          :label="a.name"
          :value="a.id"
        >
          <span>{{ a.name }}</span>
          <span class="opt-extra">{{ a.strategyTag }}{{ a.status === 'closed' ? ' · 已关闭' : '' }}</span>
        </el-option>
      </el-select>

      <template v-if="account">
        <el-tag v-if="account.isManual" size="mini" effect="plain" type="info" class="strat-tag">
          <i class="el-icon-edit-outline" /> 手动盘
        </el-tag>
        <el-tag v-else size="mini" effect="plain" class="strat-tag">
          <i class="el-icon-connection" /> {{ account.strategyTag }}
        </el-tag>
        <span v-if="account.isManual" class="muted">
          不跟踪策略 · 买卖自行下单 · 风控 止损 -{{ -thresholds.stopLoss }}% / 止盈 {{ thresholds.takeProfit }}% / 单只 ≤ {{ thresholds.maxPosition }}%
        </span>
        <template v-else>
          <span v-if="signal" class="muted">
            信号日 {{ signal.signalDate }} · 持有 {{ signal.hold }} 交易日 · 计划到期 {{ signal.exitDate || '—' }}
          </span>
          <span v-else class="warn-text">该策略无当期信号</span>
        </template>
        <span class="muted">行情 {{ quoteFreshness }}</span>
        <el-dropdown v-if="account" size="small" trigger="click" @command="onAccountCommand">
          <el-button size="small" icon="el-icon-setting" />
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="edit">编辑账户</el-dropdown-item>
            <el-dropdown-item command="toggle">{{ account.status === 'active' ? '关闭账户' : '启用账户' }}</el-dropdown-item>
            <el-dropdown-item command="remove" divided>删除账户</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </template>

      <span class="spacer" />
      <el-tag
        v-if="state"
        size="mini"
        class="session-tag"
        :type="tradeWindow.marketOpen ? 'success' : 'info'"
        effect="plain"
      >
        <i class="el-icon-time" />
        {{ sessionTagText }}
      </el-tag>
      <span v-if="state" class="muted">引擎 {{ state.asOf }}</span>
      <el-button size="small" icon="el-icon-refresh" :loading="loading" @click="loadDashboard">刷新</el-button>
      <el-button
        v-if="account"
        class="trade-btn"
        size="small"
        type="primary"
        plain
        icon="el-icon-edit-outline"
        @click="openTrade"
      >手动交易</el-button>
      <el-button size="small" type="primary" icon="el-icon-plus" @click="openCreate">新建账户</el-button>
    </div>

    <el-alert v-if="error" :title="error" type="error" show-icon :closable="false" class="sim-alert" />

    <template v-if="state">
      <!-- KPI -->
      <div class="kpi-row">
        <div class="kpi-cell">
          <span class="kpi-label">总资产</span>
          <span class="kpi-value">{{ fmtMoney(summary.equity) }}</span>
          <span class="kpi-sub">初始 {{ fmtMoney(summary.initialCash) }}</span>
        </div>
        <div class="kpi-cell">
          <span class="kpi-label">累计盈亏</span>
          <span class="kpi-value" :class="chgClass(summary.totalPnl)">
            {{ fmtSigned(summary.totalPnl) }}
          </span>
          <span class="kpi-sub" :class="chgClass(summary.totalPnlPct)">{{ fmtPct(summary.totalPnlPct) }}</span>
        </div>
        <div class="kpi-cell">
          <span class="kpi-label">今日盈亏</span>
          <span class="kpi-value" :class="chgClass(summary.dayPnl)">
            {{ fmtSigned(summary.dayPnl) }}
          </span>
          <span class="kpi-sub" :class="chgClass(summary.dayPnlPct)">{{ fmtPct(summary.dayPnlPct) }}</span>
        </div>
        <div class="kpi-cell">
          <span class="kpi-label">仓位 / 现金</span>
          <span class="kpi-value">{{ positionPct }}%</span>
          <span class="kpi-sub">{{ fmtMoney(summary.cash) }}</span>
        </div>
        <div class="kpi-cell">
          <span class="kpi-label">已实现 / 费用</span>
          <span class="kpi-value" :class="chgClass(summary.realizedPnl)">{{ fmtSigned(summary.realizedPnl) }}</span>
          <span class="kpi-sub">费用累计 {{ fmtMoney(summary.feeTotal) }}</span>
        </div>
        <div class="kpi-cell">
          <span class="kpi-label">组合回撤</span>
          <span class="kpi-value" :class="summary.drawdown <= -thresholds.drawdown ? 'down-strong' : ''">
            {{ fmtPct(summary.drawdown) }}
          </span>
          <span class="kpi-sub">持仓 {{ summary.positionCount }} 只 · 警戒 {{ thresholds.drawdown }}%</span>
        </div>
      </div>

      <!-- 待办操作 -->
      <div class="todo-card">
        <div class="todo-head" :class="{ 'is-empty': !pendingSuggestions.length }">
          <span class="dot" />
          <span class="todo-title">
            待确认操作 {{ pendingSuggestions.length }} 条
          </span>
          <span v-if="account && account.isManual" class="todo-note">
            手动盘不生成策略建议 · 买卖请在「手动交易」里自行下单
          </span>
          <span v-else-if="signal" class="todo-note">
            信号日 {{ signal.signalDate }} · 执行窗口至 {{ signal.exitDate || '—' }} · 共 {{ signal.picks.length }} 只等权
          </span>
          <span v-else class="todo-note">无信号，无法生成调仓建议</span>
          <!-- 已忽略的要显式说出来：否则用户看到「建议变少了」会以为是策略自己改了主意 -->
          <span v-if="ignoredCount" class="todo-note is-ignored">
            已忽略 {{ ignoredCount }} 条
          </span>
          <span class="spacer" />
          <el-button
            v-if="pendingSuggestions.length > 1"
            size="mini"
            type="primary"
            plain
            :loading="bulkRunning"
            @click="tradeAll"
          >全部按建议成交</el-button>
        </div>
        <div v-if="pendingSuggestions.length" class="todo-body">
          <div v-for="(s, i) in pendingSuggestions" :key="s.side + s.code + i" class="todo-row">
            <el-tag size="mini" :type="sideTagType(s.side)" effect="dark" class="side-tag">{{ SIDE_CN[s.side] }}</el-tag>
            <span class="todo-stock">
              <el-link :href="stockstarCodeLink(s.code)" target="_blank" rel="noopener noreferrer" :underline="false">
                <em>{{ s.code }}</em>
              </el-link>
              <el-link :href="eastmoneyCodeLink(s)" target="_blank" rel="noopener noreferrer" :underline="false">
                {{ s.name || '—' }}
              </el-link>
            </span>
            <span class="todo-qty">
              <template v-if="s.belowLot">
                <span class="warn-text">目标 {{ fmtMoney(s.targetAmount) }} 不足 1 手（现价 {{ s.price.toFixed(2) }}）</span>
              </template>
              <template v-else-if="s.price">
                {{ s.price.toFixed(2) }} × {{ s.shares.toLocaleString('zh-CN') }} 股
                <b>= {{ fmtMoney(s.amount) }}</b>
              </template>
              <template v-else>暂无行情，无法计算股数</template>
            </span>
            <span class="todo-reason">{{ s.reason }}</span>
            <span class="spacer" />
            <span v-if="s.affordable === false" class="warn-text">现金不足</span>
            <el-button
              size="mini"
              type="primary"
              :disabled="!s.price || !s.shares || s.affordable === false"
              :loading="tradingKey === s.side + s.code"
              @click="tradeOne(s)"
            >模拟成交</el-button>
            <el-button
              size="mini"
              :loading="ignoringKey === s.side + s.code"
              title="本期信号内不再提示这条建议（会记一条历史，可在右侧「预警」里撤销）"
              @click="ignore(s)"
            >忽略</el-button>
          </div>
        </div>
        <div v-else class="todo-empty">
          <template v-if="ignoredCount">
            本期 {{ ignoredCount }} 条建议已忽略，右侧「预警 → 已忽略的建议」可撤销
          </template>
          <template v-else>
            {{ account && account.isManual ? '手动盘无策略建议，点上方「手动交易」自行买入或卖出' : '当前没有需要调整的持仓' }}
          </template>
        </div>
      </div>

      <!-- 主体：持仓表 + 右栏 -->
      <div class="sim-body">
        <div class="panel panel-left">
          <div class="panel-head">
            <span class="panel-title">持仓明细</span>
            <span class="muted">{{ positions.length }} 只 · 市值 {{ fmtMoney(summary.marketValue) }}</span>
            <span class="spacer" />
            <el-checkbox v-model="onlyAbnormal" size="mini">仅看异常</el-checkbox>
          </div>
          <div class="table-wrap">
            <el-table
              :data="visiblePositions"
              size="mini"
              height="100%"
              :row-class-name="rowClass"
              empty-text="暂无持仓"
            >
              <el-table-column label="股票" min-width="118">
                <template slot-scope="{ row }">
                  <el-link
                    class="cell-code"
                    :href="stockstarCodeLink(row.code)"
                    target="_blank"
                    rel="noopener noreferrer"
                    :underline="false"
                  >{{ row.code }}</el-link>
                  <el-link
                    class="cell-name"
                    :href="eastmoneyCodeLink(row)"
                    target="_blank"
                    rel="noopener noreferrer"
                    :underline="false"
                  >{{ row.name }}</el-link>
                </template>
              </el-table-column>
              <el-table-column label="持股" width="72" align="right">
                <template slot-scope="{ row }">{{ row.shares.toLocaleString('zh-CN') }}</template>
              </el-table-column>
              <el-table-column label="成本" width="66" align="right">
                <template slot-scope="{ row }">{{ row.costPrice.toFixed(2) }}</template>
              </el-table-column>
              <el-table-column label="现价" width="84" align="right">
                <template slot-scope="{ row }">
                  <span class="nowrap">{{ row.price.toFixed(2) }}</span>
                  <i
                    v-if="row.priceSource !== 'realtime'"
                    class="el-icon-warning-outline stale-icon"
                    :title="row.priceSource === 'daily' ? '非实时价：以最近收盘价估值' : '无行情，以成本价估值'"
                  />
                </template>
              </el-table-column>
              <el-table-column label="浮动" width="76" align="right">
                <template slot-scope="{ row }">
                  <span :class="chgClass(row.pnlPct)">{{ fmtPct(row.pnlPct) }}</span>
                </template>
              </el-table-column>
              <el-table-column label="今日" width="70" align="right">
                <template slot-scope="{ row }">
                  <span v-if="row.pctChg == null">—</span>
                  <span v-else :class="chgClass(row.pctChg)">{{ fmtPct(row.pctChg) }}</span>
                </template>
              </el-table-column>
              <el-table-column label="市值" width="82" align="right">
                <template slot-scope="{ row }">{{ fmtMoney(row.marketValue) }}</template>
              </el-table-column>
              <el-table-column label="权重" width="60" align="right">
                <template slot-scope="{ row }">{{ row.weight.toFixed(2) }}%</template>
              </el-table-column>
              <el-table-column label="持有" width="56" align="right">
                <template slot-scope="{ row }">{{ row.heldDays }}日</template>
              </el-table-column>
              <el-table-column label="状态" width="74">
                <template slot-scope="{ row }">
                  <el-tag size="mini" :type="statusTagType(row)" effect="plain">{{ statusText(row) }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="100">
                <template slot-scope="{ row }">
                  <el-button size="mini" type="text" @click="openTrade(row)">交易</el-button>
                  <el-button size="mini" type="text" @click="openCostEdit(row)">改成本</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>

        <div class="panel panel-right">
          <el-tabs v-model="rightTab" class="right-tabs">
            <el-tab-pane :label="`预警 ${alerts.length}`" name="alerts">
              <div v-if="alerts.length" class="alert-list">
                <div v-for="a in alerts" :key="a.type + a.code" class="alert-item">
                  <span class="lv-dot" :style="{ background: levelColor(a.level) }" />
                  <div class="alert-main">
                    <div class="alert-msg" :style="{ color: levelColor(a.level) }">{{ a.message }}</div>
                    <div class="alert-detail">{{ a.detail }}</div>
                  </div>
                  <el-button
                    size="mini"
                    type="text"
                    :loading="alertHandlingKey === alertKey(a)"
                    @click="resolveAlert(a)"
                  >已处理</el-button>
                </div>
              </div>
              <div v-else class="panel-empty">
                <template v-if="alertHandledCount">
                  本期 {{ alertHandledCount }} 条预警已处理（下面「历史预警」里可撤销）。
                </template>
                <template v-else>
                  暂无预警。本账户风控阈值：止损 {{ thresholds.stopLoss }}% / 止盈 {{ thresholds.takeProfit }}% /
                  单只 ≤ {{ thresholds.maxPosition }}% / 回撤 {{ thresholds.drawdown }}%
                </template>
              </div>
              <!--
                已忽略的调仓建议。
                为什么放在「预警」页签：这里是本页唯一的「历史/流水」区（下面就是历史预警），
                用户点完「忽略」要能马上看到「确实记下来了、还能撤销」。
                为什么单独一块、不和预警混排：忽略不是风险事件，是**用户决策**，
                混进预警会让「预警 N 条」这个数变得不可信。
              -->
              <div v-if="state && state.ignores && state.ignores.length" class="history ignores">
                <div class="history-head">
                  已忽略的建议（本期生效 {{ activeIgnores.length }} 条 · 共 {{ state.ignores.length }} 条）
                </div>
                <div
                  v-for="g in state.ignores"
                  :key="'ig' + g.id"
                  class="history-item"
                  :class="{ 'is-handled': !g.active }"
                >
                  <span class="history-date">{{ g.signalDate || '—' }}</span>
                  <span class="history-msg is-plain">
                    {{ SIDE_CN[g.side] }} {{ g.code }} {{ g.name }}
                  </span>
                  <span v-if="!g.active" class="history-flag">已失效</span>
                  <el-button size="mini" type="text" class="undo-btn" @click="unignore(g)">撤销</el-button>
                </div>
              </div>

              <!--
                历史预警（风险事件台账）。
                ⚠️ 这里**必须**有操作入口：条件已不成立的旧预警不会再出现在上面的活动列表里
                   （模拟盘换过仓），但它们在账上仍是 `handled = 0`
                   ⇒ 首页「N 条模拟盘预警未处理」算的就是它们。
                   没有入口 = 清不到 0 = 待办永远挂着（2026-10-01 用户报的第二个症状）。
              -->
              <div v-if="historyAlerts.length" class="history">
                <div class="history-head">
                  <span class="history-head-text">
                    历史预警（近 {{ historyAlerts.length }} 条<template v-if="alertPendingCount"> · 未处理 {{ alertPendingCount }} 条</template>）
                  </span>
                  <el-button
                    v-if="alertPendingCount"
                    size="mini"
                    type="text"
                    class="undo-btn head-action"
                    :loading="alertBulkRunning"
                    @click="resolveAllAlerts"
                  >全部已处理</el-button>
                </div>
                <div v-for="h in historyAlerts" :key="h.id" class="history-item" :class="{ 'is-handled': h.handled }">
                  <span class="history-date">{{ h.alertDate }}</span>
                  <span class="history-msg" :style="{ color: levelColor(h.level) }">{{ h.message }}</span>
                  <el-button
                    size="mini"
                    type="text"
                    class="undo-btn"
                    :loading="alertHandlingKey === 'h' + h.id"
                    @click="resolveAlert(h, !h.handled)"
                  >{{ h.handled ? '取消处理' : '已处理' }}</el-button>
                </div>
              </div>
            </el-tab-pane>

            <el-tab-pane :label="`流水 ${trades.length}`" name="trades">
              <el-table :data="trades" size="mini" height="100%" empty-text="还没有成交记录">
                <el-table-column label="日期" prop="tradeDate" width="82" />
                <el-table-column label="方向" width="50">
                  <template slot-scope="{ row }">
                    <span :class="row.side === 'buy' ? 'up' : 'down'">{{ SIDE_CN[row.side] }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="股票" min-width="96">
                  <template slot-scope="{ row }">
                    <el-link
                      class="cell-code"
                      :href="stockstarCodeLink(row.code)"
                      target="_blank"
                      rel="noopener noreferrer"
                      :underline="false"
                    >{{ row.code }}</el-link>
                    <el-link
                      :href="eastmoneyCodeLink(row)"
                      target="_blank"
                      rel="noopener noreferrer"
                      :underline="false"
                    >{{ row.name }}</el-link>
                  </template>
                </el-table-column>
                <el-table-column label="价格" width="62" align="right">
                  <template slot-scope="{ row }">{{ row.price.toFixed(2) }}</template>
                </el-table-column>
                <el-table-column label="股数" width="66" align="right">
                  <template slot-scope="{ row }">{{ row.shares.toLocaleString('zh-CN') }}</template>
                </el-table-column>
                <el-table-column label="费用" width="58" align="right">
                  <template slot-scope="{ row }">{{ row.fee.toFixed(2) }}</template>
                </el-table-column>
                <el-table-column label="实现盈亏" width="80" align="right">
                  <template slot-scope="{ row }">
                    <span v-if="row.realizedPnl == null">—</span>
                    <span v-else :class="chgClass(row.realizedPnl)">{{ fmtSigned(row.realizedPnl) }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="依据" prop="reason" min-width="150" show-overflow-tooltip />
              </el-table>
            </el-tab-pane>

            <el-tab-pane label="权益" name="equity">
              <div class="equity-box">
                <div v-if="equityCurve.length >= 2">
                  <svg viewBox="0 0 280 96" class="spark" preserveAspectRatio="none">
                    <polyline
                      :points="equityPoints"
                      fill="none"
                      :stroke="equityUp ? '#ec0000' : '#00a800'"
                      stroke-width="1.5"
                    />
                  </svg>
                  <div class="equity-meta">
                    <span>起 {{ fmtMoney(equityCurve[0].equity) }}</span>
                    <span>今 {{ fmtMoney(equityCurve[equityCurve.length - 1].equity) }}</span>
                    <span>峰 {{ fmtMoney(summary.peakEquity) }}</span>
                  </div>
                  <div class="equity-meta">
                    <span>{{ equityCurve.length }} 个交易日</span>
                    <span :class="chgClass(summary.totalPnlPct)">累计 {{ fmtPct(summary.totalPnlPct) }}</span>
                  </div>
                </div>
                <div v-else class="panel-empty">
                  权益快照每天记录一次，明天开始就能看到曲线
                </div>
              </div>
            </el-tab-pane>
          </el-tabs>
        </div>
      </div>
    </template>

    <div v-else-if="!loading && !error" class="panel-empty standalone">
      还没有模拟账户。点右上角「新建账户」即可开始；每人只能看到自己创建的账户。
    </div>

    <!-- 新建 / 编辑账户 -->
    <el-dialog
      :title="editing ? '编辑账户' : '新建模拟账户'"
      :visible.sync="dialogVisible"
      width="520px"
      custom-class="sim-dialog"
    >
      <el-form label-width="104px" size="small">
        <el-form-item label="账户名称">
          <el-input v-model="form.name" maxlength="30" show-word-limit placeholder="如：主策略验证盘 / 我的手动盘" />
        </el-form-item>
        <el-form-item label="账户类型">
          <el-radio-group v-model="form.manual">
            <el-radio :label="false">策略盘</el-radio>
            <el-radio :label="true">手动盘</el-radio>
          </el-radio-group>
          <div class="form-hint">
            {{ form.manual
              ? '手动盘不跟踪策略：不产生调仓建议，买卖全部由你在「手动交易」里自己下；下面的风控阈值照常生效'
              : '策略盘跟踪一个滚动策略，页面按当期信号给出「调出 / 到期 / 权重偏离 / 新进」四类建议' }}
          </div>
        </el-form-item>
        <el-form-item v-if="!form.manual" label="关联策略">
          <el-select v-model="form.strategyTag" style="width: 100%" filterable placeholder="选择要跟踪的策略">
            <el-option
              v-for="s in validStrategies"
              :key="s.tag"
              :label="s.tag"
              :value="s.tag"
            >
              <span>{{ s.tag }}</span>
              <span class="opt-extra">信号 {{ s.signalDate }} · {{ s.pickCount }} 只</span>
            </el-option>
          </el-select>
          <div class="form-hint">只列出有当期信号的策略；无信号的策略无法生成建议</div>
        </el-form-item>
        <el-form-item v-if="!editing" label="初始资金">
          <el-input-number v-model="form.initialCash" :min="10000" :step="100000" :controls="false" style="width: 100%" />
        </el-form-item>

        <div class="form-section">风控阈值（按本账户生效）</div>
        <el-form-item label="止损线">
          <el-input-number
            v-model="form.stopLossPct" :min="0.1" :max="100" :step="1" :precision="1" :controls="false" style="width: 110px"
          />
          <span class="form-suffix">% 浮亏触及即报止损</span>
        </el-form-item>
        <el-form-item label="止盈线">
          <el-input-number
            v-model="form.takeProfitPct" :min="0.1" :max="1000" :step="1" :precision="1" :controls="false" style="width: 110px"
          />
          <span class="form-suffix">% 浮盈触及即报止盈</span>
        </el-form-item>
        <el-form-item label="单支持仓上限">
          <el-input-number
            v-model="form.maxPositionPct" :min="0.1" :max="100" :step="1" :precision="1" :controls="false" style="width: 110px"
          />
          <span class="form-suffix">% 单只市值占组合权益，<b>买入越限会被拒绝</b></span>
        </el-form-item>
        <el-form-item label="组合回撤">
          <el-input-number
            v-model="form.maxDrawdownPct" :min="0.1" :max="100" :step="1" :precision="1" :controls="false" style="width: 110px"
          />
          <span class="form-suffix">% 自权益高点回撤超过即报预警</span>
        </el-form-item>

        <el-form-item label="备注">
          <el-input v-model="form.note" type="textarea" :rows="2" maxlength="60" show-word-limit />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="saving" @click="saveAccount">保存</el-button>
      </div>
    </el-dialog>

    <!-- 手动交易：策略盘 / 手动盘都能用，手动盘的唯一买卖入口 -->
    <el-dialog title="手动交易" :visible.sync="tradeVisible" width="540px" custom-class="sim-dialog sim-trade-dialog">
      <el-form label-width="104px" size="small">
        <el-form-item label="账户">
          <span class="form-strong">{{ account ? account.name : '—' }}</span>
          <span class="form-suffix">
            可用现金 {{ fmtMoney(summary.cash) }} · 组合权益 {{ fmtMoney(summary.equity) }}
          </span>
        </el-form-item>
        <el-form-item label="股票代码">
          <el-input
            v-model="tradeForm.code"
            maxlength="6"
            style="width: 120px"
            placeholder="6 位代码"
            @blur="fetchQuote"
            @keyup.enter.native="fetchQuote"
          />
          <el-button size="mini" :loading="quoteLoading" @click="fetchQuote">查价</el-button>
          <span v-if="quote" class="form-suffix">
            {{ quote.name || '—' }} · {{ quote.price.toFixed(2) }}
            <em class="quote-src">{{ quote.source === 'realtime' ? '实时' : `收盘 ${quote.updatedAt}` }}</em>
          </span>
          <span v-else-if="quoteError" class="form-suffix warn-text">{{ quoteError }}</span>
        </el-form-item>
        <el-form-item label="方向">
          <el-radio-group v-model="tradeForm.side" @change="onSideChange">
            <el-radio label="buy">买入</el-radio>
            <el-radio label="sell">卖出</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="成交价">
          <el-input-number
            v-model="tradeForm.price" :min="0.01" :max="100000" :step="0.1" :precision="2" :controls="false" style="width: 120px"
          />
          <span class="form-suffix">查价自动带上最新价，可改成你的实际成交价</span>
        </el-form-item>
        <el-form-item label="股数">
          <el-input-number
            v-model="tradeForm.shares" :min="0" :max="100000000" :step="100" :precision="0" :controls="false" style="width: 120px"
          />
          <span class="form-suffix">须为 100 股整数倍</span>
          <div v-if="tradeForm.side === 'sell' && holding" class="form-hint">
            当前持有 {{ holding.shares.toLocaleString('zh-CN') }} 股 · 成本 {{ holding.costPrice.toFixed(2) }} ·
            浮动 <span :class="chgClass(holding.pnlPct)">{{ fmtPct(holding.pnlPct) }}</span>
            <el-button size="mini" type="text" @click="tradeForm.shares = holding.shares">全部卖出</el-button>
          </div>
          <div v-else-if="tradeForm.side === 'buy'" class="form-hint">
            金额约 {{ fmtMoney(tradeAmount) }} · 预估费用 {{ fmtMoney(tradeFee) }} ·
            成交后单只占比
            <b :class="overCap ? 'warn-text' : ''">
              {{ tradeWeightAfter == null ? '—' : tradeWeightAfter.toFixed(2) + '%' }}
            </b>
            <span v-if="overCap" class="warn-text">超过上限 {{ thresholds.maxPosition }}%</span>
          </div>
          <div v-else-if="tradeForm.side === 'sell'" class="form-hint warn-text">该账户没有这只股票的持仓</div>
        </el-form-item>
        <el-form-item v-if="tradeForm.side === 'buy'" label="上限">
          <el-checkbox v-model="tradeForm.force">忽略单支持仓上限（{{ thresholds.maxPosition }}%）</el-checkbox>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="tradeForm.reason" maxlength="60" placeholder="如：手动加仓 / 触发止损离场" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="tradeVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="tradeSaving" :disabled="!canTrade" @click="submitTrade">
          确认{{ SIDE_CN[tradeForm.side] }}
        </el-button>
      </div>
    </el-dialog>

    <!-- 改成本：复现实盘买入价，不改现金、不产生流水 -->
    <el-dialog title="修改成本价" :visible.sync="costVisible" width="420px" custom-class="sim-dialog">
      <el-form label-width="88px" size="small">
        <el-form-item label="股票">
          <span class="form-strong">{{ costForm.code }} {{ costForm.name }}</span>
          <span class="form-suffix">持有 {{ (costForm.shares || 0).toLocaleString('zh-CN') }} 股</span>
        </el-form-item>
        <el-form-item label="原成本">
          <span>{{ costForm.prevCost == null ? '—' : costForm.prevCost.toFixed(2) }}</span>
        </el-form-item>
        <el-form-item label="新成本" required>
          <el-input-number
            v-model="costForm.costPrice"
            :min="0.01"
            :max="100000"
            :step="0.01"
            :precision="3"
            :controls="false"
            style="width: 140px"
          />
          <span class="form-suffix">填实盘买入成本（可含费）</span>
        </el-form-item>
        <el-alert
          type="info"
          :closable="false"
          show-icon
          title="只改成本价与浮动盈亏口径，不变动现金、不产生成交流水"
        />
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="costVisible = false">取消</el-button>
        <el-button
          size="small"
          type="primary"
          :loading="costSaving"
          :disabled="!(Number(costForm.costPrice) > 0)"
          @click="submitCostEdit"
        >保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { request } from '../utils/http.js'
import { eastmoneyCodeLink, stockstarCodeLink } from '../utils/stockLink.js'

const SIDE_CN = { buy: '买入', sell: '卖出' }
// 与服务层 DEFAULT_*_PCT 对齐；仅作网络未回时的建盘占位，保存时由服务端兜底
const RISK_FALLBACK = { stopLossPct: 8, takeProfitPct: 20, maxPositionPct: 20, maxDrawdownPct: 5 }

export default {
  name: 'SimTrading',
  props: {
    visible: { type: Boolean, default: true },
    isAdmin: { type: Boolean, default: false },
  },
  data() {
    return {
      SIDE_CN,
      accounts: [],
      strategies: [],
      accountId: null,
      state: null,
      loading: false,
      error: '',
      rightTab: 'alerts',
      onlyAbnormal: false,
      // 「已忽略的建议」由服务端过滤（sim_ignore 表）—— 前端**不再**维护临时态：
      // 以前这里是 `ignored: []`，刷新就清空，于是「点了忽略又回来了」
      tradingKey: '',
      ignoringKey: '',
      /** 「已处理」/「取消处理」的 loading —— 活动预警用 `a<type><code>`，历史行用 `h<id>` */
      alertHandlingKey: '',
      /** 「全部已处理」的 loading */
      alertBulkRunning: false,
      bulkRunning: false,
      dialogVisible: false,
      editing: false,
      saving: false,
      form: {
        name: '',
        strategyTag: '',
        manual: false,
        initialCash: 1000000,
        note: '',
        ...RISK_FALLBACK,
      },
      riskDefaults: { ...RISK_FALLBACK },
      // 手动交易
      tradeVisible: false,
      tradeSaving: false,
      quoteLoading: false,
      quote: null,
      quoteError: '',
      tradeForm: { code: '', side: 'buy', price: null, shares: 0, reason: '', force: false },
      // 改成本
      costVisible: false,
      costSaving: false,
      costForm: { code: '', name: '', shares: 0, prevCost: null, costPrice: null },
      // 时段闸门会随收盘/开盘变化，页面挂着不动会停留在旧状态，定期静默刷新一次
      refreshTimer: null,
    }
  },
  computed: {
    account() {
      return this.state ? this.state.account : null
    },
    summary() {
      return (this.state && this.state.summary) || {}
    },
    thresholds() {
      return (this.state && this.state.thresholds) || { drawdown: 5 }
    },
    // 行情时段信息（仅展示；模拟成交全天候，不作为闸门）
    tradeWindow() {
      return (
        (this.state && this.state.tradeWindow) || {
          open: true,
          marketOpen: false,
          bypassed: false,
          tradingDay: true,
          reason: '',
          nextOpenAt: null,
          sessionLabel: '',
        }
      )
    },
    sessionTagText() {
      const w = this.tradeWindow
      if (w.marketOpen) return w.sessionLabel ? `行情开市 · ${w.sessionLabel}` : '行情开市'
      return w.reason ? `行情休市 · ${w.reason}` : '行情休市 · 模拟仍可下单'
    },
    signal() {
      return this.state ? this.state.signal : null
    },
    positions() {
      return (this.state && this.state.positions) || []
    },
    visiblePositions() {
      if (!this.onlyAbnormal) return this.positions
      return this.positions.filter((p) => this.statusText(p) !== '持有')
    },
    alerts() {
      return (this.state && this.state.alerts) || []
    },
    historyAlerts() {
      return ((this.state && this.state.alertHistory) || []).slice(0, 12)
    },
    /** 当日条件成立、但已被你标记「已处理」挡掉的条数 */
    alertHandledCount() {
      return (this.state && this.state.alertHandledCount) || 0
    },
    /** 台账口径：该账户全部未处理预警数（含条件已不成立的旧账）—— 首页待办算的就是它 */
    alertPendingCount() {
      return (this.state && this.state.alertPendingCount) || 0
    },
    trades() {
      return (this.state && this.state.trades) || []
    },
    equityCurve() {
      return (this.state && this.state.equityCurve) || []
    },
    equityUp() {
      const c = this.equityCurve
      return c.length < 2 ? true : c[c.length - 1].equity >= c[0].equity
    },
    equityPoints() {
      const c = this.equityCurve
      if (c.length < 2) return ''
      const vs = c.map((x) => x.equity)
      const min = Math.min(...vs)
      const max = Math.max(...vs)
      const span = max - min || 1
      return c
        .map((p, i) => `${((i / (c.length - 1)) * 280).toFixed(1)},${(92 - ((p.equity - min) / span) * 88).toFixed(1)}`)
        .join(' ')
    },
    positionPct() {
      const eq = this.summary.equity
      if (!eq) return '0.00'
      return ((this.summary.marketValue / eq) * 100).toFixed(2)
    },
    // 待确认建议：服务端已按 sim_ignore 过滤（本期信号窗口内被忽略的不再下发）
    pendingSuggestions() {
      return (this.state && this.state.suggestions) || []
    },
    // 本期（当前信号窗口）正在生效的忽略记录
    activeIgnores() {
      return ((this.state && this.state.ignores) || []).filter((x) => x.active)
    },
    /** 被忽略掉的条数（= 服务端算出的总建议数 − 实际下发数）；用来区分「本来就没建议」与「全被忽略了」 */
    ignoredCount() {
      return (this.state && this.state.ignoredCount) || 0
    },
    // 有当期信号但行情过旧的持仓，提示估值口径
    quoteFreshness() {
      const srcs = this.positions.map((p) => p.priceSource)
      if (!srcs.length) return '—'
      if (srcs.every((s) => s === 'realtime')) return '实时'
      if (srcs.every((s) => s === 'daily')) return '最近收盘'
      return '部分实时'
    },
    validStrategies() {
      return this.strategies.filter((s) => s.hasSignal)
    },
    /* ----- 手动交易辅助 ----- */
    holding() {
      const code = (this.tradeForm.code || '').replace(/\D/g, '')
      if (code.length !== 6) return null
      return this.positions.find((p) => p.code === code) || null
    },
    tradeAmount() {
      const p = Number(this.tradeForm.price)
      const s = Number(this.tradeForm.shares)
      if (!Number.isFinite(p) || !Number.isFinite(s) || p <= 0 || s <= 0) return 0
      return Math.round(p * s * 100) / 100
    },
    // 费用口径与服务端 calcFee 一致：佣金（双边，有最低）+ 印花税（仅卖出）+ 过户费
    tradeFee() {
      const a = this.account
      const amount = this.tradeAmount
      if (!a || !amount) return 0
      const commission = Math.max(amount * a.feeRate, a.minFee)
      const stamp = this.tradeForm.side === 'sell' ? amount * a.stampRate : 0
      const transfer = amount * a.transferRate
      return Math.round((commission + stamp + transfer) * 100) / 100
    },
    // 买入后单只占比（前端预估，最终以服务端 positionWeightAfter 为准）
    tradeWeightAfter() {
      if (this.tradeForm.side !== 'buy') return null
      const equity = Number(this.summary.equity) || 0
      if (!equity || !this.tradeAmount) return null
      const current = this.holding ? this.holding.marketValue : 0
      return Math.round(((current + this.tradeAmount) / equity) * 10000) / 100
    },
    overCap() {
      return this.tradeWeightAfter != null && this.tradeWeightAfter > Number(this.thresholds.maxPosition) + 0.005
    },
    canTrade() {
      if (!this.account) return false
      const code = (this.tradeForm.code || '').replace(/\D/g, '')
      const s = Number(this.tradeForm.shares)
      if (code.length !== 6 || !(Number(this.tradeForm.price) > 0)) return false
      if (!(s > 0) || s % 100 !== 0) return false
      if (this.tradeForm.side === 'sell') {
        if (!this.holding) return false
        if (s > this.holding.shares) return false
      } else if (this.tradeAmount + this.tradeFee > Number(this.summary.cash || 0) + 0.005) {
        return false
      }
      return true
    },
  },
  watch: {
    visible(val) {
      if (val && !this.state) this.load()
    },
  },
  created() {
    this.load()
  },
  mounted() {
    // 页面常驻不动的场景：跨过 11:30 / 15:00 时闸门要自己更新，否则按钮状态是错的。
    // 弹窗打开期间不刷新，避免把用户正在填的价格冲掉。
    this.refreshTimer = setInterval(() => {
      if (!this.visible || this.tradeVisible || this.loading) return
      if (typeof document !== 'undefined' && document.hidden) return
      this.loadDashboard()
    }, 60 * 1000)
  },
  beforeDestroy() {
    if (this.refreshTimer) clearInterval(this.refreshTimer)
  },
  methods: {
    eastmoneyCodeLink,
    stockstarCodeLink,    async load() {
      await this.loadAccounts()
      await this.loadDashboard()
    },
    async loadAccounts() {
      try {
        this.accounts = (await request('/api/sim/accounts')).list || []
        if (!this.accountId && this.accounts.length) this.accountId = this.accounts[0].id
      } catch (e) {
        this.error = e.message
      }
    },
    async loadStrategies() {
      if (this.strategies.length) return
      try {
        const res = await request('/api/sim/strategies')
        this.strategies = res.list || []
        // 建盘表单的风控默认值以服务端为准，避免两边默认值漂移
        if (res.defaults) this.riskDefaults = { ...RISK_FALLBACK, ...res.defaults }
      } catch (e) {
        /* 建盘时再报错 */
      }
    },
    async loadDashboard() {
      if (!this.accountId) {
        this.state = null
        return
      }
      this.loading = true
      this.error = ''
      try {
        this.state = await request(`/api/sim/dashboard?accountId=${this.accountId}`)
      } catch (e) {
        this.state = null
        this.error = e.message
      } finally {
        this.loading = false
      }
    },
    onAccountChange() {
      this.loadDashboard()
    },
    /**
     * 忽略一条建议 —— **落库**（sim_ignore），不是前端隐藏。
     *
     * 生效范围 = 当前信号窗口：服务端记下 `signal_date`，本期不再下发这条建议；
     * 下周新信号到来自动重新出现（所以不用给老记录做任何清理，它们就是历史）。
     * 接口回传**过滤好的新 state**，所以这里直接整体替换，不需要在本地维护忽略清单。
     *
     * `signalDate` 刻意不传：服务端从当期信号推导。前端传错会让忽略静默失效（对不上窗口），
     * 而这种「看不见的坏」比报错更难查。
     */
    async ignore(s) {
      this.ignoringKey = s.side + s.code
      try {
        this.state = await request('/api/sim/suggestion/ignore', {
          method: 'POST',
          body: JSON.stringify({
            accountId: this.accountId,
            code: s.code,
            side: s.side,
            name: s.name,
            reason: s.reason,
          }),
        })
        this.$message.success(`已忽略 ${s.code}${s.name ? ' ' + s.name : ''}，本期信号不再提示`)
      } catch (e) {
        this.$message.error(e.message)
      } finally {
        this.ignoringKey = ''
      }
    },
    /** 撤销一条忽略（记录可在右侧「预警 → 已忽略的建议」里找到） */
    async unignore(row) {
      try {
        this.state = await request('/api/sim/suggestion/unignore', {
          method: 'POST',
          body: JSON.stringify({ accountId: this.accountId, id: row.id }),
        })
        this.$message.success(`已恢复 ${row.code} 的建议`)
      } catch (e) {
        this.$message.error(e.message)
      }
    },
    async tradeOne(s) {
      if (!s.price || !s.shares) return
      this.tradingKey = s.side + s.code
      try {
        this.state = await request('/api/sim/trade', {
          method: 'POST',
          body: JSON.stringify({
            accountId: this.accountId,
            code: s.code,
            name: s.name,
            side: s.side,
            price: s.price,
            shares: s.shares,
            reason: s.reason,
            signalDate: this.signal ? this.signal.signalDate : '',
          }),
        })
        this.$message.success(`${SIDE_CN[s.side]} ${s.code} 已按建议成交`)
        await this.loadAccounts()
      } catch (e) {
        this.$message.error(e.message)
      } finally {
        this.tradingKey = ''
      }
    },
    // 逐条串行成交：服务端每次都会重算现金与持仓，并行会互相覆盖
    async tradeAll() {
      this.bulkRunning = true
      const list = [...this.pendingSuggestions]
      let ok = 0
      let fail = 0
      for (const s of list) {
        if (!s.price || !s.shares || s.affordable === false) {
          fail += 1
          continue
        }
        try {
          this.state = await request('/api/sim/trade', {
            method: 'POST',
            body: JSON.stringify({
              accountId: this.accountId,
              code: s.code,
              name: s.name,
              side: s.side,
              price: s.price,
              shares: s.shares,
              reason: s.reason,
              signalDate: this.signal ? this.signal.signalDate : '',
            }),
          })
          ok += 1
        } catch (e) {
          fail += 1
        }
      }
      this.bulkRunning = false
      await this.loadAccounts()
      this.$message[fail ? 'warning' : 'success'](`成交 ${ok} 笔${fail ? `，跳过/失败 ${fail} 笔` : ''}`)
    },
    async resolveAlert(a, handled = true) {
      // ⚠️ 优先用服务端在活动预警上带回来的台账 id。
      //    老写法是拿 (type, code) 去 historyAlerts 里反查，而那个列表被 slice(0, 12) 截断过
      //    ⇒ 匹配行不在前 12 条时静默 return，按钮点了没反应。
      const id = a.id != null
        ? a.id
        : (this.historyAlerts.find((h) => h.type === a.type && h.code === a.code && h.handled === !handled) || {}).id
      if (!id) {
        this.$message.warning('找不到这条预警的台账记录，请刷新后重试')
        return
      }
      this.alertHandlingKey = a.id != null ? this.alertKey(a) : 'h' + id
      try {
        const next = await request('/api/sim/alert/handle', {
          method: 'POST',
          body: JSON.stringify({ accountId: this.accountId, alertId: id, handled }),
        })
        this.state = next
        this.$message.success(handled ? '已标记处理' : '已撤销处理')
      } catch (e) {
        this.$message.error(e.message)
      } finally {
        this.alertHandlingKey = ''
      }
    },
    /** 活动预警的 loading key（列表项没有唯一 key，用 type+code 足够区分） */
    alertKey(a) {
      return `a${a.type}${a.code}`
    },
    /**
     * 一次性确认该账户全部未处理预警。
     * 用途：条件已不成立的旧预警不在活动列表里、历史列表又只渲染 12 条 ⇒ 逐条点根本点不到。
     */
    async resolveAllAlerts() {
      this.alertBulkRunning = true
      try {
        const before = this.alertPendingCount
        this.state = await request('/api/sim/alert/handle-all', {
          method: 'POST',
          body: JSON.stringify({ accountId: this.accountId }),
        })
        this.$message.success(`已确认全部 ${before} 条未处理预警（可逐条取消）`)
      } catch (e) {
        this.$message.error(e.message)
      } finally {
        this.alertBulkRunning = false
      }
    },
    openCreate() {
      this.editing = false
      this.form = {
        name: '',
        strategyTag: '',
        manual: false,
        initialCash: 1000000,
        note: '',
        ...this.riskDefaults,
      }
      this.loadStrategies()
      this.dialogVisible = true
    },
    openEdit() {
      this.editing = true
      this.form = {
        name: this.account.name,
        strategyTag: this.account.strategyTag,
        manual: !!this.account.isManual,
        initialCash: this.account.initialCash,
        note: this.account.note || '',
        stopLossPct: this.account.stopLossPct,
        takeProfitPct: this.account.takeProfitPct,
        maxPositionPct: this.account.maxPositionPct,
        maxDrawdownPct: this.account.maxDrawdownPct,
      }
      this.loadStrategies()
      this.dialogVisible = true
    },
    async saveAccount() {
      this.saving = true
      const risk = {
        stopLossPct: this.form.stopLossPct,
        takeProfitPct: this.form.takeProfitPct,
        maxPositionPct: this.form.maxPositionPct,
        maxDrawdownPct: this.form.maxDrawdownPct,
      }
      try {
        if (this.editing) {
          await request(`/api/sim/accounts/${this.accountId}`, {
            method: 'PUT',
            body: JSON.stringify({
              name: this.form.name,
              note: this.form.note,
              // 手动盘：显式告知服务端清空策略关联
              manual: this.form.manual,
              strategyTag: this.form.manual ? '' : this.form.strategyTag,
              ...risk,
            }),
          })
        } else {
          const res = await request('/api/sim/accounts', {
            method: 'POST',
            body: JSON.stringify({ ...this.form, ...risk }),
          })
          this.accountId = res.id
        }
        this.dialogVisible = false
        await this.loadAccounts()
        await this.loadDashboard()
        this.$message.success('已保存')
      } catch (e) {
        this.$message.error(e.message)
      } finally {
        this.saving = false
      }
    },
    /* ----- 手动交易 ----- */
    openTrade(row) {
      if (!this.account) return
      // ⚠️ 工具栏按钮写 @click="openTrade" 时会把 MouseEvent 当第一个参数传进来，
      // 不能只判断 row 是否 truthy，必须按「有 code 字段」识别是不是真的持仓行
      const r = row && typeof row === 'object' && row.code ? row : null
      this.quote = null
      this.quoteError = ''
      this.tradeForm = {
        code: r ? r.code : '',
        side: r ? 'sell' : 'buy',
        price: r ? r.price : null,
        shares: 0,
        reason: r ? '手动卖出' : '手动买入',
        force: false,
      }
      this.tradeVisible = true
      if (r) this.fetchQuote()
    },
    onSideChange() {
      this.tradeForm.shares = 0
      this.tradeForm.force = false
      // 卖出时若已知现价，直接用持仓的现价，少点一次查价
      if (this.tradeForm.side === 'sell' && this.holding && !this.tradeForm.price) {
        this.tradeForm.price = this.holding.price
      }
    },
    async fetchQuote() {
      const code = (this.tradeForm.code || '').replace(/\D/g, '')
      if (code.length !== 6) {
        this.quote = null
        this.quoteError = code.length ? '请输入 6 位股票代码' : ''
        return
      }
      this.quoteLoading = true
      this.quoteError = ''
      try {
        const res = await request(`/api/sim/quote?code=${code}`)
        this.quote = res.quote || null
        if (this.quote) {
          this.tradeForm.price = this.quote.price
        } else {
          this.quoteError = `未找到 ${code} 的行情（无实时快照也无日线）`
        }
      } catch (e) {
        this.quote = null
        this.quoteError = e.message
      } finally {
        this.quoteLoading = false
      }
    },
    async submitTrade() {
      // tradeSaving 在这里同步置位：按钮的 loading 要等重渲染才生效，连点两下会重复下单
      if (!this.canTrade || this.tradeSaving) return
      this.tradeSaving = true
      try {
        this.state = await request('/api/sim/trade', {
          method: 'POST',
          body: JSON.stringify({
            accountId: this.accountId,
            code: this.tradeForm.code,
            name: this.quote ? this.quote.name : '',
            side: this.tradeForm.side,
            price: this.tradeForm.price,
            shares: this.tradeForm.shares,
            reason: this.tradeForm.reason,
            force: this.tradeForm.force === true,
          }),
        })
        this.$message.success(
          `${SIDE_CN[this.tradeForm.side]} ${this.tradeForm.code} ${this.tradeForm.shares.toLocaleString('zh-CN')} 股已成交`,
        )
        this.tradeVisible = false
        await this.loadAccounts()
      } catch (e) {
        this.$message.error(e.message)
        // 被上限拦下时保留弹窗，让用户改股数或勾选忽略
        if (/单支持仓/.test(e.message)) this.tradeForm.force = false
      } finally {
        this.tradeSaving = false
      }
    },
    /* ----- 改成本（复现实盘） ----- */
    openCostEdit(row) {
      if (!row || !row.code) return
      this.costForm = {
        code: row.code,
        name: row.name || '',
        shares: row.shares,
        prevCost: row.costPrice,
        costPrice: Number(row.costPrice),
      }
      this.costVisible = true
    },
    async submitCostEdit() {
      if (this.costSaving) return
      const cost = Number(this.costForm.costPrice)
      if (!(cost > 0)) return this.$message.warning('请输入有效成本价')
      this.costSaving = true
      try {
        this.state = await request('/api/sim/position/cost', {
          method: 'PUT',
          body: JSON.stringify({
            accountId: this.accountId,
            code: this.costForm.code,
            costPrice: cost,
          }),
        })
        this.costVisible = false
        this.$message.success(`${this.costForm.code} 成本已改为 ${cost.toFixed(3)}`)
      } catch (e) {
        this.$message.error(e.message)
      } finally {
        this.costSaving = false
      }
    },
    async onAccountCommand(cmd) {
      if (cmd === 'edit') return this.openEdit()
      if (cmd === 'toggle') {
        try {
          await request(`/api/sim/accounts/${this.accountId}`, {
            method: 'PUT',
            body: JSON.stringify({ status: this.account.status === 'active' ? 'closed' : 'active' }),
          })
          await this.loadAccounts()
          await this.loadDashboard()
        } catch (e) {
          this.$message.error(e.message)
        }
        return
      }
      if (cmd === 'remove') {
        try {
          await this.$confirm(`删除账户「${this.account.name}」？持仓、流水、预警将一并清除。`, '确认删除', {
            type: 'warning',
          })
        } catch {
          return
        }
        try {
          await request(`/api/sim/accounts/${this.accountId}`, { method: 'DELETE' })
          this.accountId = null
          this.state = null
          await this.loadAccounts()
          await this.loadDashboard()
          this.$message.success('已删除')
        } catch (e) {
          this.$message.error(e.message)
        }
      }
    },
    /* 展示辅助 —— A 股口径：涨红跌绿 */
    chgClass(v) {
      const n = Number(v)
      if (!Number.isFinite(n) || n === 0) return ''
      return n > 0 ? 'up' : 'down'
    },
    fmtMoney(v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return '—'
      return `¥${n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    fmtSigned(v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return '—'
      return `${n > 0 ? '+' : n < 0 ? '−' : ''}¥${Math.abs(n).toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    },
    fmtPct(v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return '—'
      return `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toFixed(2)}%`
    },
    sideTagType(side) {
      return side === 'buy' ? 'danger' : 'success'
    },
    levelColor(level) {
      return { danger: '#A32D2D', warn: '#BA7517', info: '#185FA5' }[level] || '#5F5E5A'
    },
    statusText(row) {
      const th = this.thresholds
      if (row.pnlPct <= Number(th.stopLoss != null ? th.stopLoss : -8)) return '止损'
      if (row.weight > Number(th.maxPosition || 20) + 0.005) return '超配'
      if (row.exitDue) return '到期'
      const s = this.pendingSuggestions.find((x) => x.code === row.code && x.side === 'sell')
      if (s) return '待调出'
      if (row.pctChg != null && Math.abs(row.pctChg) >= Number(th.surge || 9.5)) return '异动'
      return '持有'
    },
    statusTagType(row) {
      const t = this.statusText(row)
      if (t === '持有') return 'info'
      if (t === '异动' || t === '超配') return 'warning'
      return 'danger'
    },
    rowClass({ row }) {
      return this.statusText(row) === '持有' ? '' : 'row-abnormal'
    },
  },
}
</script>

<style scoped>
.sim-trading {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 96px);
  min-width: 0;
  gap: 10px;
}

.sim-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.spacer {
  flex: 1 1 auto;
}

.muted {
  font-size: 12px;
  color: #909399;
}

.warn-text {
  font-size: 12px;
  color: #e6a23c;
}

.strat-tag {
  font-size: 12px;
}

/* 行情时段状态（仅信息，不拦下单） */
.session-tag {
  font-size: 12px;
  white-space: nowrap;
}

.opt-extra {
  float: right;
  font-size: 12px;
  color: #909399;
  margin-left: 12px;
}

.sim-alert {
  margin-bottom: 0;
}

.kpi-row {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 8px;
}

.kpi-cell {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fff;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.kpi-label {
  font-size: 12px;
  color: #909399;
}

.kpi-value {
  font-size: 17px;
  font-weight: 500;
  color: #303133;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kpi-sub {
  font-size: 11px;
  color: #a8abb2;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.todo-card {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fff;
  overflow: hidden;
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
}

.todo-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  background: #fdf6ec;
  border-bottom: 1px solid #faecd8;
}

.todo-head.is-empty {
  background: #f4f4f5;
  border-bottom-color: #e9e9eb;
}

.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #e6a23c;
}

.todo-head.is-empty .dot {
  background: #c0c4cc;
}

.todo-title {
  font-size: 13px;
  font-weight: 500;
  color: #b88230;
}

.todo-head.is-empty .todo-title {
  color: #909399;
}

.todo-note {
  font-size: 12px;
  color: #c8a06a;
}

.todo-head.is-empty .todo-note {
  color: #a8abb2;
}

.todo-body {
  overflow-y: auto;
  min-height: 0;
  max-height: 136px;
}

.todo-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 12px;
  border-bottom: 1px solid #f5f7fa;
  font-size: 12px;
}

.todo-row:last-child {
  border-bottom: none;
}

.side-tag {
  flex: 0 0 auto;
}

.todo-stock {
  flex: 0 0 132px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.todo-stock em {
  font-style: normal;
  color: #909399;
  margin-right: 4px;
}

.todo-qty {
  flex: 0 0 210px;
  color: #606266;
}

.todo-qty b {
  font-weight: 500;
  color: #303133;
}

.todo-reason {
  flex: 1 1 auto;
  min-width: 0;
  color: #909399;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.todo-empty {
  padding: 16px 12px;
  text-align: center;
  font-size: 12px;
  color: #909399;
}

.sim-body {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  gap: 10px;
}

.panel {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fff;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.panel-left {
  flex: 1 1 0;
  min-width: 0;
}

.panel-right {
  flex: 0 0 340px;
}

.panel-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid #f2f6fc;
}

.panel-title {
  font-size: 13px;
  font-weight: 500;
  color: #303133;
}

.table-wrap {
  flex: 1 1 0;
  min-height: 0;
}

.right-tabs {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.right-tabs >>> .el-tabs__header {
  flex: 0 0 auto;
  margin: 8px 12px;
}

.right-tabs >>> .el-tabs__nav-wrap::after {
  height: 1px;
}

.right-tabs >>> .el-tabs__content {
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
  padding: 0 12px 8px;
}

.right-tabs >>> .el-tab-pane {
  height: 100%;
  overflow-y: auto;
}

.alert-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.alert-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f5f7fa;
}

.lv-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  margin-top: 5px;
  flex: 0 0 auto;
}

.alert-main {
  flex: 1 1 auto;
  min-width: 0;
}

.alert-msg {
  font-size: 12px;
  line-height: 1.5;
}

.alert-detail {
  font-size: 11px;
  color: #a8abb2;
  margin-top: 2px;
}

.history {
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px dashed #ebeef5;
}

.history-head {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
}

.history-head-text {
  flex: 1 1 auto;
  min-width: 0;
}

/* 标题行右侧的动作（如「全部已处理」）—— 靠 margin-left:auto 顶到最右 */
.history-head .head-action {
  flex: 0 0 auto;
  margin-left: auto;
}

.history-item {
  display: flex;
  gap: 6px;
  font-size: 11px;
  padding: 3px 0;
  color: #606266;
}

.history-item.is-handled {
  opacity: 0.45;
}

.history-date {
  flex: 0 0 66px;
  color: #a8abb2;
}

.history-msg {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.history-flag {
  flex: 0 0 auto;
  color: #a8abb2;
}

/* 「已忽略的建议」区块：沿用 .history 版式，只加撤销按钮的位置与一条左侧色条做区分 */
.history.ignores {
  border-top-style: solid;
  border-top-color: #e4e7ed;
}
.history.ignores .history-item {
  align-items: center;
}
/* 忽略条目是「决策记录」，不是风险文案 ⇒ 不要 levelColor 的红/黄，保持中性 */
.history-msg.is-plain {
  color: #606266;
}
.undo-btn {
  flex: 0 0 auto;
  padding: 0;
  height: auto;
  font-size: 11px;
}
/* 待确认操作条上的「已忽略 N 条」 */
.todo-note.is-ignored {
  color: #e6a23c;
  background: #fdf6ec;
  border-radius: 3px;
  padding: 1px 6px;
}

.equity-box {
  padding-top: 4px;
}

.spark {
  width: 100%;
  height: 96px;
  border-bottom: 1px solid #ebeef5;
}

.equity-meta {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #909399;
  margin-top: 6px;
}

.panel-empty {
  padding: 16px 4px;
  font-size: 12px;
  color: #909399;
  line-height: 1.7;
}

.panel-empty.standalone {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fff;
  text-align: center;
  padding: 48px 12px;
}

.form-hint {
  font-size: 12px;
  color: #a8abb2;
  line-height: 1.5;
  margin-top: 2px;
}

/* 对话框内：分组标题 / 行内补充说明 / 强调值 */
.form-section {
  font-size: 12px;
  font-weight: 600;
  color: #606266;
  margin: 2px 0 10px;
  padding-bottom: 5px;
  border-bottom: 1px solid #ebeef5;
}

.form-suffix {
  font-size: 12px;
  color: #909399;
  margin-left: 8px;
  line-height: 1.4;
}

.form-strong {
  font-weight: 600;
  color: #303133;
}

.quote-src {
  font-style: normal;
  color: #a8abb2;
}

.cell-code {
  color: #909399;
  margin-right: 5px;
  font-size: 12px;
  vertical-align: baseline;
}

.cell-name {
  color: #303133;
  font-size: 12px;
  vertical-align: baseline;
}

.todo-stock .el-link {
  margin-right: 4px;
  font-size: inherit;
  vertical-align: baseline;
}

.todo-stock .el-link em {
  font-style: normal;
  font-weight: 600;
  color: #606266;
}

.stale-icon {
  color: #e6a23c;
  margin-left: 3px;
  font-size: 12px;
}

.nowrap {
  white-space: nowrap;
}

.up {
  color: #ec0000;
}

.down {
  color: #00a800;
}

.down-strong {
  color: #00a800;
}

.sim-trading >>> .row-abnormal {
  background: #fdf6ec;
}

@media (max-width: 1440px) {
  .panel-right {
    flex: 0 0 300px;
  }
}

@media (max-width: 1200px) {
  .kpi-row {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  /* 移动端为简化入口：不锁定视口高度，改为纵向堆叠（见 App.vue 的 m-extra-wrap） */
  .sim-trading {
    height: auto;
    min-height: 60vh;
  }

  .sim-body {
    flex-direction: column;
  }

  .panel-left,
  .panel-right {
    flex: 0 0 auto;
  }

  .todo-stock,
  .todo-qty {
    flex: 0 0 auto;
  }
}
</style>

<!-- el-dialog 挂在 body 上，scoped 选择器够不到，只能按 custom-class 写全局样式 -->
<style>
@media (max-width: 768px) {
  .sim-dialog {
    width: 94% !important;
    margin-top: 6vh !important;
  }

  .sim-dialog .el-dialog__body {
    padding: 12px 14px;
  }

  .sim-dialog .el-form-item__label {
    width: 84px !important;
  }

  .sim-dialog .el-form-item__content {
    margin-left: 84px !important;
  }

  .sim-dialog .form-suffix {
    display: block;
    margin-left: 0;
  }
}
</style>
